import { useQuery } from '@apollo/client';
import { graphql, useStaticQuery } from 'gatsby';
import { find, propEq } from 'ramda';
import React, { FC, useContext, useEffect } from 'react';

import LoadingWrapper from '@ui/atoms/LoadingWrapper';

import { dataConstants } from '@ui/routes/Membership/MembershipInsuranceProviderStep/MembershipInsuranceProviderStep.data';

import useMemberInsuranceInfo, { IMemberInsurance } from '@hooks/useMemberInsurance';

import { keysToCamel } from '@utils/butter';

import FlowContext from '@providers/FlowContextProvider';

import { IFlowStepProps } from '@folx-types/flow-steps';
import { IAccountInfoQueryData } from '@folx-types/member';

import ACCOUNT_INFO_QUERY from '@graphql/queries/AccountInfo.query.gql';

import InsuranceDetailsStep, {
	IInsuranceDetailsStepContent,
} from './MembershipInsuranceDetailsStep.component';

interface IInsuranceDetailsStepPageProps extends IFlowStepProps {}

const InsurancDetailsStepPage: FC<IInsuranceDetailsStepPageProps> = ({ path, stepKey }) => {
	const { pageContent } = useStaticQuery(graphql`
		{
			pageContent: butterPage(slug: { eq: "membership-insurance-details-step" }) {
				title
				description
				tooltip
				tooltip_text
				first_name_label
				last_name_label
				member_id_label
				group_label
				is_policyholder_label
				policyholder_first_name_label
				policyholder_last_name_label
				policyholder_relationship_label
				policyholder_dob_label
			}
		}
	`);

	const { existingFormData, handleFormSubmission, handleNavBack } = useContext(FlowContext);

	const { data: accountData, loading: accountLoading } =
		useQuery<IAccountInfoQueryData>(ACCOUNT_INFO_QUERY);

	const { memberInsurance, loading: memberInsuranceLoading } = useMemberInsuranceInfo(
		false,
		'network-only'
	);

	useEffect(() => {
		if (!existingFormData?.[dataConstants.insuranceProvider]) {
			handleNavBack(stepKey);
		}
	}, []);

	const member = accountData?.currentMember;
	const insurance = find<IMemberInsurance, IMemberInsurance>(
		propEq('insuranceProviderUuid', existingFormData?.[dataConstants.insuranceProvider]),
		memberInsurance ?? []
	);

	const refinedContent: IInsuranceDetailsStepContent = keysToCamel(
		pageContent
	) as IInsuranceDetailsStepContent;

	const submit = (id: string, firstName: string, lastName: string) => {
		handleFormSubmission(
			{ memberInsuranceId: id, insuranceFirstName: firstName, insuranceLastName: lastName },
			stepKey
		);
	};

	return (
		<LoadingWrapper loading={memberInsuranceLoading || accountLoading}>
			<InsuranceDetailsStep
				pageContent={refinedContent}
				member={member}
				selectedInsurance={insurance}
				path={path}
				stepKey={stepKey}
				existingFormData={existingFormData}
				handleFormSubmission={handleFormSubmission}
				handleNavBack={handleNavBack}
				provider={{
					id: existingFormData?.[dataConstants.insuranceProvider],
					name: existingFormData?.[dataConstants.insuranceProviderName],
				}}
				submit={submit}
			/>
		</LoadingWrapper>
	);
};

export default InsurancDetailsStepPage;
